<template>
  <div>
    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-3" />
    <div v-else>
      <v-alert v-if="errorMessage" type="error" class="mb-3">
        {{ errorMessage }}
      </v-alert>

      <v-row class="mb-1 ncrm-record-history__toolbar">
        <v-col cols="12" md="3">
          <div class="ncrm-record-history__filter-label text-caption text-medium-emphasis">
            {{ $t("ncrm.contacts.details.history.filters.sourceLabel") }}
          </div>
          <v-select
            v-model="eventSourceFilter"
            :items="historySourceFilterItems"
            item-title="label"
            item-value="value"
            hide-details
            class="ncrm-record-history__filter-control"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="ncrm-record-history__filter-label text-caption text-medium-emphasis">
            {{ $t("ncrm.contacts.details.history.filters.eventTypeLabel") }}
          </div>
          <v-select
            v-model="eventTypeFilter"
            :items="historyEventTypeFilterItems"
            item-title="label"
            item-value="value"
            hide-details
            class="ncrm-record-history__filter-control"
          />
        </v-col>
        <v-col cols="12" md="2">
          <div class="ncrm-record-history__filter-label text-caption text-medium-emphasis">
            {{ $t("ncrm.contacts.details.history.filters.authorLabel") }}
          </div>
          <v-text-field
            v-model="authorFilter"
            :placeholder="$t('ncrm.contacts.details.history.filters.authorLabel')"
            hide-details
            clearable
            class="ncrm-record-history__filter-control"
          />
        </v-col>
        <v-col cols="6" md="2">
          <div class="ncrm-record-history__filter-label text-caption text-medium-emphasis">
            {{ $t("ncrm.contacts.details.history.filters.fromDateLabel") }}
          </div>
          <v-text-field
            v-model="fromDate"
            type="date"
            hide-details
            class="ncrm-record-history__filter-control ncrm-record-history__date-filter-control"
          />
        </v-col>
        <v-col cols="6" md="2">
          <div class="ncrm-record-history__filter-label text-caption text-medium-emphasis">
            {{ $t("ncrm.contacts.details.history.filters.toDateLabel") }}
          </div>
          <v-text-field
            v-model="toDate"
            type="date"
            hide-details
            class="ncrm-record-history__filter-control ncrm-record-history__date-filter-control"
          />
        </v-col>
        <v-col cols="12" class="d-flex align-center justify-end ga-2">
          <v-btn size="small" variant="text" color="primary" @click="clearFilters">
            {{ $t("ncrm.contacts.details.history.filters.clear") }}
          </v-btn>
          <v-btn size="small" variant="text" color="primary" @click="fetchHistory({ force: true })">
            <v-icon start size="16">mdi-refresh</v-icon>
            {{ $t("ncrm.contacts.details.history.refresh") }}
          </v-btn>
        </v-col>
      </v-row>

      <v-list v-if="groupedItems.length" class="bg-transparent py-0 ncrm-record-history__list">
        <template v-for="(group, groupIndex) in groupedItems" :key="`record-history-group-${group.key}`">
          <v-list-subheader class="px-0 text-uppercase text-caption text-medium-emphasis">
            {{ group.label }}
          </v-list-subheader>
          <template v-for="(historyItem, index) in group.items" :key="historyItem.id || index">
            <v-list-item class="px-0">
              <v-list-item-title class="text-body-2 ncrm-text-strong d-flex align-center ga-2 flex-wrap">
                <v-chip size="x-small" :color="historyEventColor(historyItem.event_type)">
                  {{ historyEventLabel(historyItem.event_type) }}
                </v-chip>
                <v-chip size="x-small" variant="outlined">
                  {{ historySourceLabel(historyItem.event_source) }}
                </v-chip>
              </v-list-item-title>
              <v-list-item-subtitle class="text-caption text-medium-emphasis mt-1">
                {{ $t("ncrm.contacts.details.history.changedByAt", {
                  user: historyChangedByLabel(historyItem),
                  date: formatDateTime(historyItem.created_at),
                }) }}
              </v-list-item-subtitle>
              <v-list-item-subtitle v-if="hasHistoryValueChange(historyItem)" class="text-body-2 mt-2">
                <span class="text-medium-emphasis">
                  {{ $t("ncrm.contacts.details.history.from") }}:
                </span>
                <span>{{ historyItem.from_value || emptyValueLabel }}</span>
                <span class="mx-2 text-medium-emphasis">→</span>
                <span class="text-medium-emphasis">
                  {{ $t("ncrm.contacts.details.history.to") }}:
                </span>
                <span>{{ historyItem.to_value || emptyValueLabel }}</span>
              </v-list-item-subtitle>
            </v-list-item>
            <v-divider v-if="index < group.items.length - 1" />
          </template>
          <v-divider v-if="groupIndex < groupedItems.length - 1" class="my-2" />
        </template>
      </v-list>
      <v-alert v-else type="info">
        {{ $t("ncrm.contacts.details.history.empty") }}
      </v-alert>
      <div v-if="hasMoreItems" class="d-flex justify-center mt-3">
        <v-btn
          size="small"
          variant="text"
          color="primary"
          :loading="loadingMore"
          :disabled="loading || loadingMore"
          @click="loadMoreHistory"
        >
          {{ $t("ncrm.contacts.details.history.loadMore") }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

const HISTORY_EVENT_COLORS = Object.freeze({
  stage_changed: "primary",
  owner_changed: "info",
  status_changed: "info",
  converted_to_deal: "success",
  lead_auto_converted_to_deal: "success",
  deal_created: "primary",
  deal_updated: "info",
  deal_owner_changed: "info",
  deal_value_changed: "info",
  deal_expected_close_date_changed: "info",
  deal_stage_changed: "primary",
  deal_status_changed: "warning",
  deal_deleted: "error",
  deal_board_created: "primary",
  deal_board_updated: "info",
  deal_board_member_added: "success",
  deal_board_member_removed: "warning",
  deal_stage_added: "success",
  deal_stage_updated: "info",
  deal_stage_removed: "warning",
  deal_stage_reordered: "info",
  deal_line_item_added: "success",
  deal_line_item_updated: "info",
  deal_line_item_removed: "warning",
  activity_created: "primary",
  activity_updated: "info",
  activity_completed: "success",
  activity_reopened: "warning",
  activity_deleted: "error",
});

const normalizeText = (value) => String(value ?? "").trim();

const normalizeHistoryEventType = (value) => normalizeText(value).toLowerCase().replace(/\s+/g, "_");

const normalizeHistoryEventSource = (value) => {
  const normalized = normalizeText(value).toLowerCase();
  if (!normalized) return "lead";
  if (["lead", "activity", "deal"].includes(normalized)) return normalized;
  return normalized;
};

const parsePositiveId = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const normalized = Math.trunc(parsed);
  return normalized > 0 ? normalized : null;
};

const parseDateToMs = (value) => {
  const normalized = normalizeText(value);
  if (!normalized) return null;
  const parsed = new Date(normalized);
  const timestamp = parsed.getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
};

export default {
  name: "NcrmRecordHistoryTimeline",
  props: {
    entityType: {
      type: String,
      default: "",
    },
    entityId: {
      type: [String, Number],
      default: "",
    },
  },
  data() {
    return {
      loading: false,
      loaded: false,
      errorMessage: "",
      items: [],
      eventSourceFilter: "all",
      eventTypeFilter: "all",
      authorFilter: "",
      fromDate: "",
      toDate: "",
      pageSize: 100,
      currentPage: 0,
      totalItems: 0,
      loadingMore: false,
      suppressServerFilterReload: false,
    };
  },
  computed: {
    emptyValueLabel() {
      return this.$t("ncrm.contacts.details.emptyValue");
    },
    resolvedEntityType() {
      const normalized = normalizeText(this.entityType).toLowerCase();
      return ["company", "person", "deal"].includes(normalized) ? normalized : "";
    },
    resolvedEntityId() {
      return parsePositiveId(this.entityId);
    },
    hasValidEntityTarget() {
      return Boolean(this.resolvedEntityType && this.resolvedEntityId);
    },
    historySourceFilterItems() {
      return [
        {
          value: "all",
          label: this.$t("ncrm.contacts.details.history.filters.allSources"),
        },
        {
          value: "lead",
          label: this.$t("ncrm.contacts.details.history.filters.sources.lead"),
        },
        {
          value: "activity",
          label: this.$t("ncrm.contacts.details.history.filters.sources.activity"),
        },
        {
          value: "deal",
          label: this.$t("ncrm.contacts.details.history.filters.sources.deal"),
        },
      ];
    },
    historyEventTypeFilterItems() {
      const map = new Map();
      for (const item of this.items) {
        const eventType = normalizeHistoryEventType(item?.event_type);
        if (!eventType || map.has(eventType)) continue;
        map.set(eventType, {
          value: eventType,
          label: this.historyEventLabel(eventType),
        });
      }

      return [
        {
          value: "all",
          label: this.$t("ncrm.contacts.details.history.filters.allEvents"),
        },
        ...Array.from(map.values()).sort((a, b) => String(a.label).localeCompare(String(b.label))),
      ];
    },
    hasMoreItems() {
      return this.totalItems > this.items.length;
    },
    filteredItems() {
      const authorFilter = normalizeText(this.authorFilter).toLowerCase();

      return (this.items || []).filter((item) => {
        if (authorFilter) {
          const authorName = normalizeText(item?.changed_by_name).toLowerCase();
          const authorId = normalizeText(item?.changed_by_user_id).toLowerCase();
          if (!authorName.includes(authorFilter) && !authorId.includes(authorFilter)) {
            return false;
          }
        }

        return true;
      });
    },
    groupedItems() {
      const groups = new Map();
      for (const item of this.filteredItems) {
        const key = this.historyGroupKey(item?.created_at);
        if (!groups.has(key)) {
          groups.set(key, {
            key,
            label: this.historyGroupLabel(key),
            items: [],
          });
        }
        groups.get(key).items.push(item);
      }

      return Array.from(groups.values()).sort((a, b) => {
        const aTs = parseDateToMs(a.key);
        const bTs = parseDateToMs(b.key);
        return (bTs || 0) - (aTs || 0);
      });
    },
  },
  watch: {
    entityType() {
      this.reloadHistory();
    },
    entityId() {
      this.reloadHistory();
    },
    eventSourceFilter() {
      this.handleServerFilterChanged();
    },
    eventTypeFilter() {
      this.handleServerFilterChanged();
    },
    fromDate() {
      this.handleServerFilterChanged();
    },
    toDate() {
      this.handleServerFilterChanged();
    },
    authorFilter(newValue, oldValue) {
      if (parsePositiveId(newValue) !== parsePositiveId(oldValue)) {
        this.handleServerFilterChanged();
      }
    },
  },
  mounted() {
    this.fetchHistory();
  },
  methods: {
    hasTranslation(key) {
      if (typeof this.$te !== "function") return false;
      return this.$te(key);
    },
    applyDefaultFilters() {
      this.suppressServerFilterReload = true;
      this.eventSourceFilter = "all";
      this.eventTypeFilter = "all";
      this.authorFilter = "";
      this.fromDate = "";
      this.toDate = "";
      const release = () => {
        this.suppressServerFilterReload = false;
      };
      if (typeof this.$nextTick === "function") {
        this.$nextTick(release);
      } else {
        release();
      }
    },
    historyEventColor(eventType) {
      const normalized = normalizeHistoryEventType(eventType);
      return HISTORY_EVENT_COLORS[normalized] || "default";
    },
    historyEventLabel(eventType) {
      const normalized = normalizeHistoryEventType(eventType);
      if (!normalized) return this.$t("ncrm.contacts.details.history.eventTypes.updated");
      const key = `ncrm.contacts.details.history.eventTypes.${normalized}`;
      if (this.hasTranslation(key)) return this.$t(key);
      return normalized;
    },
    historySourceLabel(eventSource) {
      const normalized = normalizeHistoryEventSource(eventSource);
      const key = `ncrm.contacts.details.history.filters.sources.${normalized}`;
      if (this.hasTranslation(key)) return this.$t(key);
      return normalized;
    },
    historyChangedByLabel(item = {}) {
      return normalizeText(item?.changed_by_name) || item?.changed_by_user_id || this.$t("ncrm.contacts.details.history.unknownAuthor");
    },
    hasHistoryValueChange(item = {}) {
      const fromValue = normalizeText(item?.from_value);
      const toValue = normalizeText(item?.to_value);
      return Boolean(fromValue || toValue);
    },
    historyGroupKey(value) {
      const timestamp = parseDateToMs(value);
      if (timestamp === null) return "unknown";
      const parsed = new Date(timestamp);
      return parsed.toISOString().slice(0, 10);
    },
    historyGroupLabel(groupKey) {
      if (!groupKey || groupKey === "unknown") {
        return this.$t("ncrm.contacts.details.history.unknownDateGroup");
      }
      const parsed = new Date(`${groupKey}T00:00:00`);
      if (Number.isNaN(parsed.getTime())) {
        return this.$t("ncrm.contacts.details.history.unknownDateGroup");
      }
      const dd = String(parsed.getDate()).padStart(2, "0");
      const mm = String(parsed.getMonth() + 1).padStart(2, "0");
      const yyyy = parsed.getFullYear();
      return `${dd}.${mm}.${yyyy}`;
    },
    formatDateTime(value) {
      const timestamp = parseDateToMs(value);
      if (timestamp === null) return this.emptyValueLabel;
      const d = new Date(timestamp);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      const HH = String(d.getHours()).padStart(2, "0");
      const min = String(d.getMinutes()).padStart(2, "0");
      return `${dd}.${mm}.${yyyy}, ${HH}:${min}`;
    },
    clearFilters() {
      const hasChanges = this.eventSourceFilter !== "all" ||
        this.eventTypeFilter !== "all" ||
        normalizeText(this.authorFilter) ||
        normalizeText(this.fromDate) ||
        normalizeText(this.toDate);

      this.applyDefaultFilters();

      if (hasChanges && this.hasValidEntityTarget) {
        this.fetchHistory({ force: true, reset: true });
      }
    },
    resetHistoryState() {
      this.items = [];
      this.loaded = false;
      this.errorMessage = "";
      this.totalItems = 0;
      this.currentPage = 0;
      this.applyDefaultFilters();
    },
    reloadHistory() {
      this.applyDefaultFilters();
      this.items = [];
      this.loaded = false;
      this.errorMessage = "";
      this.totalItems = 0;
      this.currentPage = 0;
      this.fetchHistory({ force: true, reset: true });
    },
    handleServerFilterChanged() {
      if (this.suppressServerFilterReload) return;
      if (!this.hasValidEntityTarget) return;
      this.fetchHistory({ force: true, reset: true });
    },
    buildHistoryRequestParams({ page }) {
      const params = {
        entityType: this.resolvedEntityType,
        entityId: this.resolvedEntityId,
        limit: this.pageSize,
        page,
      };

      const eventSource = normalizeHistoryEventSource(this.eventSourceFilter);
      if (eventSource && eventSource !== "all") {
        params.eventSource = eventSource;
      }

      const eventType = normalizeHistoryEventType(this.eventTypeFilter);
      if (eventType && eventType !== "all") {
        params.eventType = eventType;
      }

      const fromDate = normalizeText(this.fromDate);
      if (fromDate) {
        params.fromDate = fromDate;
      }

      const toDate = normalizeText(this.toDate);
      if (toDate) {
        params.toDate = toDate;
      }

      const changedByUserId = parsePositiveId(this.authorFilter);
      if (changedByUserId) {
        params.changedByUserId = changedByUserId;
      }

      return params;
    },
    loadMoreHistory() {
      this.fetchHistory({ force: true, append: true });
    },
    async fetchHistory({ force = false, reset = false, append = false } = {}) {
      if (!this.hasValidEntityTarget) {
        this.resetHistoryState();
        return;
      }
      if (this.loading || this.loadingMore) return;
      if (this.loaded && !force && !append) return;
      if (append && this.loaded && this.totalItems <= this.items.length) return;

      const requestPage = append ? this.currentPage + 1 : 1;
      if (!append && reset) {
        this.items = [];
        this.totalItems = 0;
        this.currentPage = 0;
      }

      if (append) {
        this.loadingMore = true;
      } else {
        this.loading = true;
      }
      this.errorMessage = "";
      try {
        const { data } = await axios.get("/ncrm/activity/history", {
          params: this.buildHistoryRequestParams({ page: requestPage }),
        });

        const incomingItems = Array.isArray(data?.items) ? data.items : [];
        this.items = append ? [...this.items, ...incomingItems] : incomingItems;
        const rawTotal = Number(data?.total);
        const normalizedTotal = Number.isFinite(rawTotal) && rawTotal >= 0 ? Math.trunc(rawTotal) : this.items.length;
        this.totalItems = Math.max(normalizedTotal, this.items.length);

        const rawPage = Number(data?.page);
        this.currentPage = Number.isFinite(rawPage) && rawPage > 0 ? Math.trunc(rawPage) : requestPage;

        const rawPerPage = Number(data?.perPage);
        if (Number.isFinite(rawPerPage) && rawPerPage > 0) {
          this.pageSize = Math.trunc(rawPerPage);
        }
        this.loaded = true;
      } catch (error) {
        console.error("[ncrm-record-history] load error:", error);
        if (!append) {
          this.items = [];
          this.loaded = false;
          this.totalItems = 0;
          this.currentPage = 0;
        }
        this.errorMessage = error?.response?.data?.error || this.$t("ncrm.contacts.details.history.loadError");
      } finally {
        if (append) {
          this.loadingMore = false;
        } else {
          this.loading = false;
        }
      }
    },
  },
};
</script>

<style scoped>
.ncrm-record-history__list :deep(.v-list-item-subtitle) {
  white-space: normal;
}

.ncrm-record-history__list :deep(.v-list-subheader) {
  min-height: 20px;
  padding-top: 0;
}

.ncrm-record-history__toolbar {
  align-items: end;
  padding-top: 4px;
  overflow: visible;
}

.ncrm-record-history__filter-label {
  line-height: 1.2;
  margin-bottom: 4px;
}

.ncrm-record-history__date-filter-control :deep(input[type="date"]) {
  min-height: 24px;
  padding-right: 4px;
}
</style>
